<template>
  <jd-tooltip
    ref="tooltipRef"
    :visible="popperVisible"
    :teleported="teleported"
    :popper-class="[nsCascader.e('dropdown'), popperClass]"
    :popper-options="popperOptions"
    :fallback-placements="fallbackPlacements"
    :stop-popper-mouse-event="false"
    :gpu-acceleration="false"
    :placement="placement"
    :transition="`${nsCascader.namespace.value}-zoom-in-top`"
    effect="light"
    pure
    persistent
    :show-arrow="false"
    :offset="4"
    @hide="hideSuggestionPanel"
  >
    <template #default>
      <div
        v-clickoutside:[contentRef]="() => togglePopperVisible(false)"
        :class="cascaderKls"
        :style="cascaderStyle"
        @click="() => togglePopperVisible(readonly ? undefined : true)"
        @keydown="handleKeyDown"
        @mouseenter="inputHover = true"
        @mouseleave="inputHover = false"
      >
        <jd-input
          ref="input"
          v-model="inputValue"
          :placeholder="currentPlaceholder"
          :readonly="readonly"
          :disabled="isDisabled"
          :validate-event="false"
          :size="realSize"
          :class="inputClass"
          :tabindex="multiple && filterable && !isDisabled ? -1 : undefined"
          :surface="surface"
          :round="round"
          @compositionstart="handleComposition"
          @compositionupdate="handleComposition"
          @compositionend="handleComposition"
          @focus="handleFocus"
          @blur="handleBlur"
          @input="handleInput"
        >
          <template #suffix>
            <jd-icon
              v-if="clearBtnVisible"
              key="clear"
              :class="[nsInput.e('icon'), 'icon-circle-close']"
              @click.stop="handleClear"
            >
              <circle-close />
            </jd-icon>
            <jd-icon
              v-else
              key="arrow-down"
              :class="cascaderIconKls"
              @click.stop="togglePopperVisible()"
            >
              <arrow-down />
            </jd-icon>
          </template>
        </jd-input>

        <div
          v-if="multiple"
          ref="tagWrapper"
          :class="[
            nsCascader.e('tags'),
            multipleLimit > 0 ? nsCascader.e('multipleLimit') : '',
          ]"
          :style="tagWrapperStyle"
        >
          <div :style="{ width: multipleLimit > 0 ? '82%' : '100%' }">
            <jd-tag
              v-for="(tag, index) in presentTags"
              :key="tag.key"
              :type="tagType"
              :size="tagSize"
              :hit="tag.hitState"
              :closable="tag.closable"
              :style="getTagStyle(index)"
              :class="nsCascader.e('tag')"
              disable-transitions
              @close="deleteTag(tag)"
            >
              <template v-if="tag.isCollapseTag === false">
                <span>{{ tag.text }}</span>
              </template>
              <template v-else>
                <jd-tooltip
                  :disabled="popperVisible || !collapseTagsTooltip"
                  :fallback-placements="['bottom', 'top', 'right', 'left']"
                  placement="bottom"
                  effect="light"
                  v-bind="{ ...collapseTagsTooltipProps }"
                >
                  <template #default>
                    <span>{{ tag.text }}</span>
                  </template>
                  <template #content>
                    <jd-scrollbar
                      :class="nsCascader.e('collapse-tags')"
                      :style="{ maxWidth: tagsTooltipsMaxWidth }"
                      :max-height="tagsTooltipsMaxHeight"
                    >
                      <div
                        v-for="(tag2, idx) in allPresentTags.slice(
                          dynamicMaxCollapseTags || 1
                        )"
                        :key="idx"
                        :class="nsCascader.e('collapse-tag')"
                      >
                        <jd-tag
                          :key="tag2.key"
                          class="in-tooltip"
                          :type="tagType"
                          :size="tagSize"
                          :hit="tag2.hitState"
                          :closable="tag2.closable"
                          disable-transitions
                          @close="deleteTag(tag2)"
                        >
                          <span>{{ tag2.text }}</span>
                        </jd-tag>
                      </div>
                    </jd-scrollbar>
                  </template>
                </jd-tooltip>
              </template>
            </jd-tag>
          </div>
          <jd-tag
            v-if="multipleLimit > 0 && checkedNodes.length > 0"
            :type="tagType"
            :size="tagSize"
            :hit="false"
            :closable="false"
            :class="nsCascader.e('tag')"
            disable-transitions
          >
            <span>{{ checkedNodes.length }}/{{ multipleLimit }}</span>
          </jd-tag>
          <input
            v-if="!searchOnPanel && filterable && !isDisabled"
            v-model="searchInputValue"
            type="text"
            :class="nsCascader.e('search-input')"
            :placeholder="searchInputPlaceholder"
            @input="(e) => handleInput(searchInputValue, e)"
            @click.stop="togglePopperVisible(true)"
            @keydown.delete="handleDelete"
            @compositionstart="handleComposition"
            @compositionupdate="handleComposition"
            @compositionend="handleComposition"
            @focus="handleFocus"
            @blur="handleBlur"
          />
        </div>
      </div>
    </template>

    <template #content>
      <div
        v-if="
          $slots.header ||
          (searchOnPanel && filterable && !isDisabled) ||
          $slots['header-top'] ||
          $slots['header-bottom']
        "
        :class="[nsCascader.e('header')]"
      >
        <slot name="header-top" />
        <div
          v-if="$slots.header || searchOnPanel"
          :class="[nsCascader.e('header-content')]"
        >
          <slot name="header">
            <jd-input
              v-if="searchOnPanel && filterable && !isDisabled"
              v-model="searchPanelInputValue"
              type="text"
              :placeholder="searchPlaceholder"
              :suffix-icon="Search"
              @input="handleSearch"
              @compositionstart="handleComposition"
              @compositionupdate="handleComposition"
              @compositionend="handleComposition"
            />
          </slot>
        </div>
        <slot name="header-bottom" />
      </div>
      <jd-cascader-panel
        v-show="!filtering"
        ref="cascaderPanelRef"
        v-model="checkedValue"
        :options="options"
        :props="props.props"
        :border="false"
        :render-label="$slots.default"
        :multiple-all="multipleAll"
        :multiple-all-text="multipleAllText"
        :multiple-all-disabled="
          multipleAllDisabled || multipleAllDisabledByLimit
        "
        :multiple-all-count="multipleAllCount"
        :multiple-all-level="multipleAllLevel"
        :expand-disabled="expandDisabled"
        :panels-config="panelsConfig"
        :multiple-limit="props.multipleLimit"
        :checked-nodes-count="checkedNodes.length"
        @expand-change="handleExpandChange"
        @checked-change="handleCheckedChange"
        @close="$nextTick(() => togglePopperVisible(false))"
      >
        <template #empty>
          <slot name="empty" />
        </template>
      </jd-cascader-panel>
      <jd-scrollbar
        v-if="filterable"
        v-show="filtering"
        ref="suggestionPanel"
        tag="ul"
        :class="nsCascader.e('suggestion-panel')"
        :view-class="nsCascader.e('suggestion-list')"
        @keydown="handleSuggestionKeyDown"
      >
        <template v-if="suggestions.length">
          <li
            v-for="item in suggestions"
            :key="item.uid"
            :class="[
              nsCascader.e('suggestion-item'),
              nsCascader.is('checked', item.checked),
            ]"
            :tabindex="-1"
            @click="handleSuggestionClick(item)"
          >
            <span
              v-if="highlightMatchText"
              v-html="formatMatchText(item.suggestionText)"
            />
            <span v-else>{{ item.suggestionText }}</span>
            <jd-icon v-if="item.checked">
              <check />
            </jd-icon>
          </li>
        </template>
        <slot v-else name="empty">
          <JdEmpty :image-size="80" :description="t('el.cascader.noMatch')" />
        </slot>
      </jd-scrollbar>
      <div v-if="$slots.extra" :class="[nsCascader.e('extra')]">
        <slot name="extra" />
      </div>
    </template>
  </jd-tooltip>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, useAttrs, watch } from 'vue'
import { isPromise } from '@vue/shared'
import { cloneDeep, debounce } from 'lodash-unified'
import { useCssVar, useResizeObserver } from '@vueuse/core'
import {
  AngleDown as ArrowDown,
  Check,
  Close as CircleClose,
  Search,
} from '@jd/jdesign-icons-vue'
import JdCascaderPanel from '@jdesign-vue/components/cascader-panel'
import JdInput from '@jdesign-vue/components/input'
import JdTooltip from '@jdesign-vue/components/tooltip'
import JdScrollbar from '@jdesign-vue/components/scrollbar'
import JdTag from '@jdesign-vue/components/tag'
import JdIcon from '@jdesign-vue/components/icon'
import JdEmpty from '@jdesign-vue/components/empty'
import { useFormItem, useFormSize } from '@jdesign-vue/components/form'
import { ClickOutside as vClickoutside } from '@jdesign-vue/directives'
import { useLocale, useNamespace } from '@jdesign-vue/hooks'
import {
  debugWarn,
  focusNode,
  getSibling,
  isClient,
  isKorean,
} from '@jdesign-vue/utils'
import {
  CHANGE_EVENT,
  // CHECKED_CHANGE_EVENT,
  EVENT_CODE,
  UPDATE_MODEL_EVENT,
} from '@jdesign-vue/constants'
import { CascaderStrategy, cascaderEmits, cascaderProps } from './cascader'

import { useMaxCollapesTags } from './useMaxCollapesTags'
import type { Options } from '@jdesign-vue/components/popper'
import type { ComputedRef, Ref, StyleValue } from 'vue'
import type { TooltipInstance } from '@jdesign-vue/components/tooltip'
import type { InputInstance } from '@jdesign-vue/components/input'
import type { ScrollbarInstance } from '@jdesign-vue/components/scrollbar'
import type {
  CascaderNode,
  CascaderPanelInstance,
  CascaderValue,
  Tag,
} from '@jdesign-vue/components/cascader-panel'

const popperOptions: Partial<Options> = {
  modifiers: [
    {
      name: 'arrowPosition',
      enabled: true,
      phase: 'main',
      fn: ({ state }) => {
        // const { modifiersData, placement } = state as any
        // if (['right', 'left', 'bottom', 'top'].includes(placement)) return
        // modifiersData?.arrow?.x = 35
      },
      requires: ['arrow'],
    },
  ],
}
const COMPONENT_NAME = 'JdCascader'

defineOptions({
  name: COMPONENT_NAME,
})

const props = defineProps(cascaderProps)
const emit = defineEmits(cascaderEmits)
const attrs = useAttrs()

let inputInitialHeight = 0
let pressDeleteCount = 0

const nsCascader = useNamespace('cascader')
const nsInput = useNamespace('input')

const { t } = useLocale()
const { form, formItem } = useFormItem()

const tooltipRef: Ref<TooltipInstance | null> = ref(null)
const input: Ref<InputInstance | null> = ref(null)
const tagWrapper = ref(null)
const cascaderPanelRef: Ref<CascaderPanelInstance | null> = ref(null)
const suggestionPanel: Ref<ScrollbarInstance | null> = ref(null)
const popperVisible = ref(false)
const inputHover = ref(false)
const filtering = ref(false)
const filterFocus = ref(false)
const inputValue = ref('')
const searchInputValue = ref('')
const searchPanelInputValue = ref('')
const presentTags: Ref<Tag[]> = ref([])
const allPresentTags: Ref<Tag[]> = ref([])
const suggestions: Ref<CascaderNode[]> = ref([])
const isOnComposition = ref(false)

const cascaderStyle = computed<StyleValue>(() => {
  return attrs.style as StyleValue
})

const tagWrapperStyle = computed(() => {
  return props.collapseTags && props.collapseTagsNoWrap
    ? { flexWrap: 'nowrap' }
    : {}
})

const isDisabled = computed(() => props.disabled || form?.disabled)
const inputPlaceholder = computed(
  () => props.placeholder || t('el.cascader.placeholder')
)
const searchInputPlaceholder = computed(() => {
  return presentText.value || allPresentTags.value?.length
    ? ''
    : inputPlaceholder.value
})
const currentPlaceholder = computed(() =>
  searchInputValue.value ||
  presentTags.value.length > 0 ||
  isOnComposition.value
    ? ''
    : inputPlaceholder.value
)
const realSize = useFormSize()
const tagSize = computed(() =>
  ['small'].includes(realSize.value) ? 'small' : 'default'
)
const multiple = computed(() => !!props.props.multiple)
const multipleAll = computed(() => !!props.props.multipleAll)
const multipleAllText = computed(() => props.props.multipleAllText || '全部')
const multipleAllDisabled = computed(() => !!props.props.multipleAllDisabled)
const multipleAllCount = computed(() => props.props.multipleAllCount)
const multipleAllLevel = computed(() => !!props.props.multipleAllLevel)
const expandDisabled = computed(() => props.expandDisabled)
const readonly = computed(() => !props.filterable || multiple.value)
const multipleAllDisabledByLimit = computed(() => {
  // 当multipleLimit大于0时，禁用全选按钮
  return props.multipleLimit > 0
})
const searchKeyword = computed(() =>
  props.searchOnPanel
    ? searchPanelInputValue.value
    : multiple.value
    ? searchInputValue.value
    : inputValue.value
)
const checkedNodes: ComputedRef<CascaderNode[]> = computed(
  () => cascaderPanelRef.value?.checkedNodes || []
)
const clearBtnVisible = computed(() => {
  if (
    !props.clearable ||
    isDisabled.value ||
    filtering.value ||
    !inputHover.value
  )
    return false

  return !!checkedNodes.value.length
})
const presentText = computed(() => {
  const { showAllLevels, separator } = props
  const nodes = checkedNodes.value
  return nodes.length
    ? multiple.value
      ? ''
      : nodes[0].calcText(showAllLevels, separator)
    : ''
})

const checkedValue = computed<CascaderValue>({
  get() {
    return cloneDeep(props.modelValue) as CascaderValue
  },
  set(val) {
    emit(UPDATE_MODEL_EVENT, val)
    emit(CHANGE_EVENT, val)
    if (props.validateEvent) {
      formItem?.validate('change').catch((err) => debugWarn(err))
    }
  },
})

const cascaderKls = computed(() => {
  return [
    nsCascader.b(),
    nsCascader.m(realSize.value),
    nsCascader.is('disabled', isDisabled.value),
    nsCascader.is('surface', props.surface),
    attrs.class,
  ]
})

const cascaderIconKls = computed(() => {
  return [
    nsInput.e('icon'),
    'icon-arrow-down',
    nsCascader.is('reverse', popperVisible.value),
  ]
})

const inputClass = computed(() => {
  return nsCascader.is('focus', popperVisible.value || filterFocus.value)
})

const contentRef = computed(() => {
  return tooltipRef.value?.popperRef?.contentRef
})

const togglePopperVisible = (visible?: boolean, e?: any) => {
  if (isDisabled.value) return

  visible = visible ?? !popperVisible.value

  if (visible !== popperVisible.value) {
    popperVisible.value = visible
    input.value?.input?.setAttribute('aria-expanded', `${visible}`)

    if (visible) {
      updatePopperPosition()
      nextTick(cascaderPanelRef.value?.scrollToExpandingNode)
    } else if (props.filterable) {
      syncPresentTextValue()
    }

    emit('visibleChange', visible)
  }

  if (popperVisible.value && e && e.target) {
    popperVisible.value = false
    e.target.focus()
  }
}

const updatePopperPosition = () => {
  nextTick(() => {
    tooltipRef.value?.updatePopper()
  })
}

const hideSuggestionPanel = () => {
  filtering.value = false
}

const genTag = (node: CascaderNode): Tag => {
  const { showAllLevels, separator } = props
  return {
    node,
    key: node.uid,
    text: node.calcText(showAllLevels, separator),
    hitState: false,
    closable: !isDisabled.value && !node.isDisabled,
    isCollapseTag: false,
  }
}

const deleteTag = (tag: Tag) => {
  const node = tag.node as CascaderNode
  node.doCheck(false)
  cascaderPanelRef.value?.calculateCheckedValue()
  emit('removeTag', node.valueByOption)
}

const { dynamicMaxCollapseTags, tagMaxWidth } = useMaxCollapesTags({
  checkedNodes,
  tagWrapper,
  maxCollapseTags: computed(() => props.maxCollapseTags),
})

const getTagStyle = (index: number) => {
  // 获取标签的最大宽度，多选且开启折叠标签时，需要设置标签的最大宽度
  const maxWidthStyle = tagMaxWidth.value
    ? {
        maxWidth: `${tagMaxWidth.value}px`,
      }
    : undefined

  return index === 0 && props.collapseTags && props.collapseTagsNoWrap
    ? {
        flex: '1 1 auto',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...maxWidthStyle,
      }
    : {
        ...maxWidthStyle,
      }
}

/**
 * @description 获取顶层选中的节点
 */
function getParentStrategyNodes() {
  // 处理级联选择器值，只返回顶层节点
  const currentCheckedNodes: CascaderNode[] =
    cascaderPanelRef.value?.getCheckedNodes(false) || []
  // 所有选项顶层节点
  const currentTopLevelNodes: Record<number | string, CascaderNode> = {}
  currentCheckedNodes?.forEach((node) => {
    // 如果节点被选中，并且父节点不存在/未被选中，则将节点添加到顶层节点中
    if (node?.checked && !node.parent?.checked) {
      currentTopLevelNodes[node.value] = node
    }
  })
  return Object.values(currentTopLevelNodes)
}

/**
 * @description 计算presentTags
 * @param isUpdate 是否是折叠tag更新，如果isUpdate为true，则不重新计算动态折叠tag数量
 */
function calculatePresentTags() {
  if (!multiple.value) return

  let nodes = checkedNodes.value

  if (props.showCheckedStrategy === CascaderStrategy.SHOW_PARENT) {
    nodes = getParentStrategyNodes()
  }
  const tags: Tag[] = []

  const allTags: Tag[] = []
  nodes.forEach((node) => allTags.push(genTag(node)))
  allPresentTags.value = allTags

  if (nodes.length) {
    const showNodes = nodes.slice(0, dynamicMaxCollapseTags.value)
    const rest = nodes.slice(dynamicMaxCollapseTags.value)
    const restCount = rest.length
    showNodes.forEach((node) => tags.push(genTag(node)))
    //判断是否设置最大限制数量
    const maxLimit: number = props.multipleLimit
    if (restCount) {
      if (props.collapseTags) {
        if (maxLimit === 0) {
          tags.push({
            key: -1,
            text: `+ ${restCount}`,
            closable: false,
            isCollapseTag: true,
          })
        }
      } else {
        rest.forEach((node) => tags.push(genTag(node)))
      }
    }
  }

  presentTags.value = tags
}

const calculateSuggestions = async () => {
  const { filterMethod, separator } = props
  const res = cascaderPanelRef.value
    ?.getFlattedNodes(!props.props.checkStrictly && !props.props.multiple)
    ?.filter((node) => {
      if (node.isDisabled) return false
      node.calcSuggestionText(separator)
      return filterMethod(node, searchKeyword.value)
    })

  if (multiple.value) {
    presentTags.value.forEach((tag) => {
      tag.hitState = false
    })
    allPresentTags.value.forEach((tag) => {
      tag.hitState = false
    })
  }

  filtering.value = true
  suggestions.value = res!
  await nextTick()
  updatePopperPosition()
}

const focusFirstNode = () => {
  let firstNode!: HTMLElement

  if (filtering.value && suggestionPanel.value) {
    firstNode = suggestionPanel.value.$el.querySelector(
      `.${nsCascader.e('suggestion-item')}`
    )
  } else {
    firstNode = cascaderPanelRef.value?.$el.querySelector(
      `.${nsCascader.b('node')}[tabindex="-1"]`
    )
  }

  if (firstNode) {
    firstNode.focus()
    !filtering.value && firstNode.click()
  }
}

const updateStyle = () => {
  const inputInner = input.value?.input
  const tagWrapperEl = tagWrapper.value
  const suggestionPanelEl = suggestionPanel.value?.$el

  if (!isClient || !inputInner) return

  if (suggestionPanelEl) {
    const suggestionList = suggestionPanelEl.querySelector(
      `.${nsCascader.e('suggestion-list')}`
    )
    suggestionList.style.minWidth = `${inputInner.offsetWidth}px`
  }

  if (tagWrapperEl) {
    const { offsetHeight } = tagWrapperEl
    const height =
      presentTags.value.length > 0
        ? `${Math.max(offsetHeight + 4, inputInitialHeight)}px`
        : `${inputInitialHeight}px`
    inputInner.style.height = height
    updatePopperPosition()
  }
}

const getCheckedNodes = (leafOnly: boolean) => {
  return cascaderPanelRef.value?.getCheckedNodes(leafOnly)
}

const handleExpandChange = (value: CascaderValue) => {
  updatePopperPosition()
  emit('expandChange', value)
}

const handleCheckedChange = (param: any) => {
  // 当multipleLimit大于0时，限制选中数量
  if (
    props.multipleLimit > 0 &&
    checkedNodes.value.length > props.multipleLimit
  ) {
    // 获取当前选中的节点
    const currentCheckedNodes = checkedNodes.value
    // 保留前multipleLimit个选中的节点，取消其余的
    const nodesToUncheck = currentCheckedNodes.slice(props.multipleLimit)
    nodesToUncheck.forEach((node) => {
      node.doCheck(false)
    })
    // 重新计算选中值
    cascaderPanelRef.value?.calculateCheckedValue()
  }
  emit('checkedChange', param)
}

const handleComposition = (event: CompositionEvent) => {
  const text = (event.target as HTMLInputElement)?.value
  if (event.type === 'compositionend') {
    isOnComposition.value = false
    nextTick(() =>
      props.searchOnPanel ? handleSearch(text) : handleInput(text)
    )
  } else {
    const lastCharacter = text[text.length - 1] || ''
    isOnComposition.value = !isKorean(lastCharacter)
  }
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (isOnComposition.value) return

  switch (e.code) {
    case EVENT_CODE.enter:
      togglePopperVisible()
      break
    case EVENT_CODE.down:
      togglePopperVisible(true)
      nextTick(focusFirstNode)
      e.preventDefault()
      break
    case EVENT_CODE.esc:
      if (popperVisible.value === true) {
        e.preventDefault()
        e.stopPropagation()
        togglePopperVisible(false)
      }
      break
    case EVENT_CODE.tab:
      togglePopperVisible(false)
      break
  }
}

const handleClear = () => {
  cascaderPanelRef.value?.clearCheckedNodes()
  if (!popperVisible.value && props.filterable) {
    syncPresentTextValue()
  }
  togglePopperVisible(false)
}

const syncPresentTextValue = () => {
  const { value } = presentText
  inputValue.value = value
  searchInputValue.value = value
  searchPanelInputValue.value = ''
}

const handleSuggestionClick = (node: CascaderNode) => {
  const { checked } = node

  if (multiple.value) {
    cascaderPanelRef.value?.handleCheckChange(node, !checked, false)
  } else {
    !checked && cascaderPanelRef.value?.handleCheckChange(node, true, false)
    togglePopperVisible(false)
  }
}

const handleSuggestionKeyDown = (e: KeyboardEvent) => {
  const target = e.target as HTMLElement
  const { code } = e

  switch (code) {
    case EVENT_CODE.up:
    case EVENT_CODE.down: {
      const distance = code === EVENT_CODE.up ? -1 : 1
      focusNode(
        getSibling(
          target,
          distance,
          `.${nsCascader.e('suggestion-item')}[tabindex="-1"]`
        ) as HTMLElement
      )
      break
    }
    case EVENT_CODE.enter:
      target.click()
      break
  }
}

const handleDelete = () => {
  const tags = presentTags.value
  const lastTag = tags[tags.length - 1]
  pressDeleteCount = searchInputValue.value ? 0 : pressDeleteCount + 1

  if (!lastTag || !pressDeleteCount || (props.collapseTags && tags.length > 1))
    return

  if (lastTag.hitState) {
    deleteTag(lastTag)
  } else {
    lastTag.hitState = true
  }
}

const handleFocus = (e: FocusEvent) => {
  const el = e.target as HTMLInputElement
  const name = nsCascader.e('search-input')
  if (el.className === name) {
    filterFocus.value = true
  }
  emit('focus', e)
}

const handleBlur = (e: FocusEvent) => {
  filterFocus.value = false
  emit('blur', e)
}

const handleFilter = debounce(() => {
  const { value } = searchKeyword

  if (!value) return

  const passed = props.beforeFilter(value)

  if (isPromise(passed)) {
    passed.then(calculateSuggestions).catch(() => {
      /* prevent log error */
    })
  } else if (passed !== false) {
    calculateSuggestions()
  } else {
    hideSuggestionPanel()
  }
}, props.debounce)

const handleInput = (val: string, e?: KeyboardEvent) => {
  if (props.searchOnPanel) return

  !popperVisible.value && togglePopperVisible(true)

  if (e?.isComposing) return

  val ? handleFilter() : hideSuggestionPanel()
}

const getInputInnerHeight = (inputInner: HTMLElement): number =>
  Number.parseFloat(
    useCssVar(nsInput.cssVarName('input-height'), inputInner).value
  ) - 2

const handleSearch = (val: string, e?: KeyboardEvent) => {
  if (!props.searchOnPanel) return

  if (e?.isComposing) return

  val ? handleFilter() : hideSuggestionPanel()
}

const formatMatchText = (item: string) => {
  const queryString = String(searchKeyword.value)
  if (queryString?.length > 0) {
    const regex = new RegExp(queryString, 'g')
    return item?.replace(
      regex,
      `<span class='highlight-match-text'>${queryString}</span>`
    )
  }
  return item
}

watch(filtering, updatePopperPosition)

watch([checkedNodes, isDisabled, dynamicMaxCollapseTags], calculatePresentTags)

watch(presentTags, () => {
  nextTick(() => updateStyle())
})

watch(realSize, async () => {
  await nextTick()
  const inputInner = input.value!.input!
  inputInitialHeight = getInputInnerHeight(inputInner) || inputInitialHeight
  updateStyle()
})

watch(presentText, syncPresentTextValue, { immediate: true })

onMounted(() => {
  const inputInner = input.value!.input!

  const inputInnerHeight = getInputInnerHeight(inputInner)

  inputInitialHeight = inputInner.offsetHeight || inputInnerHeight
  useResizeObserver(inputInner, updateStyle)
})

/**
 * @description 批量设置节点选中状态
 * @param paths 顶级节点id数组
 */
function selectNodes(paths: any[]) {
  const panel = cascaderPanelRef.value
  const flatNodes = panel?.getFlattedNodes(false)
  const flatPaths = Array.from(
    new Set(paths?.reduce?.((acc, path) => acc.concat(path), []) || [])
  )
  flatPaths?.forEach((path) => {
    const node = flatNodes?.find((node) => String(node.value) === String(path))
    if (
      node &&
      !node.checked &&
      !node.config?.expandDisabled &&
      !node.isDisabled
    ) {
      panel?.handleCheckChange(node, true)
    }
  })
}

/**
 * @description 处理多选场景下全选和取消全选
 * @param value 是否全选
 */
const selectAllNodes = (value: boolean) => {
  if (!props?.props?.multiple) return

  // 获取级联选择器面板
  const panel = cascaderPanelRef.value
  if (!panel?.menus?.[0]?.length) return
  // 遍历所有节点
  panel.menus[0]?.forEach((node) => {
    // 如果节点已选中，或者节点不可展开，则跳过
    if (
      value === node.checked ||
      (!node.config.expandDisabled && node.isDisabled)
    )
      return
    // 处理节点选中状态
    panel.handleCheckChange(node, value)
  })
}

defineExpose({
  /**
   * @description get an array of currently selected node,(leafOnly) whether only return the leaf checked nodes, default is `false`
   */
  getCheckedNodes,
  /**
   * @description cascader panel ref
   */
  cascaderPanelRef,
  /**
   * @description toggle the visible of popper
   */
  togglePopperVisible,
  /**
   * @description cascader content ref
   */
  contentRef,
  /**
   * @description 获取合并的父节点（当父节点的所有子节点都被选中时，只返回父节点）
   */
  getParentStrategyNodes,
  /**
   * @description 批量设置节点选中状态
   */
  selectNodes,
  /**
   * @description 处理多选场景下全选和取消全选
   */
  selectAllNodes,
})
</script>
